import React, {useState, useEffect, useMemo} from 'react';
import {
    CardContent,
    Grid,
    Typography,
    Radio,
    Button as MUIButton,
    Tooltip,
    Select,
    MenuItem,
    TextField,
    InputLabel,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {Button} from '@/components/ui/button';
import {Product} from '@amplify/athena-models';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {useHistory, useParams, generatePath} from 'react-router-dom';
import {categoryOneLiners, categoryFullScripts, productFallbackTriggers} from '../productConstants';
import RootStore from '../../../Core/mobx/RootStore';
import {PIVOT_REASONS, PivotReasonOption} from '../../../Core/constants/pivotReasons';
import {productCodesToLegacyKeys, getCarrierProductFromCode} from '../../../Core/utils/productCodeMapping';
import {ClientQuoteData} from '@/APIs/CRM';
import CustomerPortalAPI, {Customer} from '@/APIs/CustomerPortalAPI';
import {InfoOutlined, Star as StarIcon} from '@material-ui/icons';

const useStyles = makeStyles({
    container: {
        marginTop: 23,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    sectionTitleBox: {
        display: 'flex',
        alignItems: 'center',
    },
    sectionNumber: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 46,
        width: 46,
        borderRadius: '50%',
        backgroundColor: '#E9ECFF',
        color: '#3F51B5',
        marginRight: 21,
        fontSize: 26,
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        textAlign: 'center',
    },
    sectionTitle: {
        fontSize: 24,
        fontWeight: 700,
        fontFamily: 'LoraSemiBold',
        lineHeight: '120%',
        letterSpacing: 0,
    },
    saveAndUpdateButton: {
        marginTop: 28,
        padding: '10.5px 13.5px',
        fontFamily: 'FredokaMedium',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '150%',
    },
    blueInfoBox: {
        backgroundColor: '#E9ECFF',
        borderRadius: '6px 6px 0 0',
        padding: '18px 50.5px 25px',
        border: '1px solid #E7E7E7',
    },
    selectedProductTitle: {
        fontSize: 24,
        fontWeight: 600,
        fontFamily: 'LoraSemiBold',
        lineHeight: '30px',
    },
    productNameWithInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#3F51B5',
        marginBottom: 7,
    },
    infoIcon: {
        marginRight: 6,
        color: '#3F51B5',
        fontSize: 18,
        cursor: 'pointer',
    },
    scriptText: {
        fontSize: 18,
        fontWeight: 500,
        fontFamily: 'LoraItalic',
        lineHeight: '30px',
        color: '#3F51B5',
    },
    fallbackSection: {
        backgroundColor: '#F5F5F5',
        padding: '15px 20px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        borderLeft: '1px solid #E7E7E7',
        borderRight: '1px solid #E7E7E7',
    },
    fallbackText: {
        fontSize: 16,
        color: '#838383',
        lineHeight: '160%',
        fontFamily: 'FredokaRegular',
        flexWrap: 'wrap',
        gap: 8,
    },
    productListSection: {
        border: '1px solid #E7E7E7',
        borderRadius: '0 0 6px 6px',
        padding: '12px 44px 32px',
    },
    productCategory: {
        marginBottom: 16,
    },
    productCategoryTitle: {
        fontFamily: 'FredokaMedium',
        fontSize: 18,
        fontWeight: 500,
        color: '#434343',
        lineHeight: '180%',
    },
    productOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '4px 0',
        fontFamily: 'FredokaRegular',
        fontSize: 18,
        lineHeight: '180%',
        position: 'relative',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#F9F9F9',
        },
    },
    selectedProduct: {
        '& .MuiRadio-root': {
            color: '#3F51B5',
        },
        backgroundColor: '#E9ECFF',
        border: '1px solid #3F51B5',
    },
    disabledProduct: {
        opacity: 0.5,
        cursor: 'not-allowed',
        backgroundColor: '#F5F5F5',
    },
    goldStar: {
        color: '#FFD700',
        fontSize: 20,
        position: 'absolute',
        left: -24,
    },
    continueButton: {
        marginTop: 28,
        width: '100%',
        padding: '10.5px 13.5px',
        fontFamily: 'FredokaMedium',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '150%',
    },
    tooltip: {
        fontSize: 14,
        lineHeight: 1.6,
        maxWidth: 500,
        padding: '10px 14px',
    },
    pivotReflexive: {
        marginTop: 20,
    },
    pivotLabel: {
        fontFamily: 'FredokaMedium',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '160%',
        color: '#838383',
        marginBottom: 8,
    },
    recommendationItem: {
        marginTop: 22,
        padding: '10px 12px 20px',
        border: '1px solid #E7E7E7',
        borderRadius: 6,
        color: '#838383',
    },
    recommendationItemTitle: {
        color: '#434343',
        fontSize: 24,
        fontWeight: 600,
        fontFamily: 'LoraSemiBold',
        lineHeight: '180%',
        textTransform: 'capitalize',
    },
    radioButton: {
        padding: 0,
        paddingRight: 8,
    },
    radioButtonSelected: {
        padding: 0,
        paddingRight: 8,
        color: '#3F51B5',
    },
});

interface BestFitProductProps {
    parameterStore: ParameterStore;
    customerQuoteData?: ClientQuoteData[];
    onSave: (selectedProduct?: string) => Promise<void>;
    loading: boolean;
}

interface AllProductType extends Product {
    recommended?: boolean;
}

export const BestFitProduct: React.FC<BestFitProductProps> = ({parameterStore, customerQuoteData, onSave, loading}) => {
    const classes = useStyles();
    const history = useHistory();
    const params = useParams<any>();
    const [selectedProduct, setSelectedProduct] = useState<string>('');
    const [pivotReason, setPivotReason] = useState<string>('');
    const [pivotOtherReason, setPivotOtherReason] = useState<string>('');
    const [showPivotReflexive, setShowPivotReflexive] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const {prequal, qualifiedProducts, recommendedProductsResponse} = parameterStore || {};

    const primaryProduct = prequal?.recommendedProductPrimary?.value || '';
    const fallbackProduct = prequal?.recommendedProductFallback?.value || '';
    const fallbackTriggers = prequal?.recommendedProductFallbackTriggers?.value || '';

    // Map fallback product types to display names
    const fallbackDisplayNames: {[key: string]: string} = {
        Term: 'Term',
        IUL: 'IUL',
        'Whole Life': 'Whole Life',
        Annuity: 'Annuity',
        ADB: 'ADB',
        VUL: 'VUL',
    };

    // Get all products from recommendedProductsResponse
    const allProducts =
        recommendedProductsResponse?.allProducts || recommendedProductsResponse?.products || qualifiedProducts || [];

    // Log to debug the loading issue
    useEffect(() => {
        console.log('BestFitProduct products loading state:', {
            hasRecommendedResponse: !!recommendedProductsResponse,
            allProductsCount: allProducts.length,
            qualifiedProductsCount: qualifiedProducts?.length || 0,
        });
    }, [recommendedProductsResponse, allProducts.length, qualifiedProducts]);
    const eligibleProductCodes = recommendedProductsResponse?.allEligibleProductCodes || [];

    // Products to exclude from display
    const excludedProducts = ['LSWIUL', 'LSWTERM'];

    // Sort all products by productRank (highest rank first) and filter out excluded products
    const sortedProducts = [...allProducts]
        .filter((p: any) => !excludedProducts.includes(p.code))
        .sort((a, b) => {
            const rankA = (a as any).productRank || 0;
            const rankB = (b as any).productRank || 0;
            return rankB - rankA;
        });

    // Group products by type, maintaining sort order and filtering out ineligible products
    const groupedProducts = useMemo(() => {
        const [iulProducts, vulProducts, termProducts, finalExpenseProducts, annuityProducts, adbProducts] = [
            'IUL',
            'VUL',
            'TERM',
            'FEXP',
            'ANNUITY',
            'ADB',
        ].map((type) => {
            // Filter products by type and eligibility while maintaining the rank order
            const products = sortedProducts
                .filter((rp: any) => rp.productTypeCode === type)
                .filter((p: any) => eligibleProductCodes.includes(p.code));
            return products;
        });

        return {iulProducts, vulProducts, termProducts, finalExpenseProducts, annuityProducts, adbProducts};
    }, [sortedProducts, eligibleProductCodes]);

    const {iulProducts, vulProducts, termProducts, finalExpenseProducts, annuityProducts, adbProducts} =
        groupedProducts;

    const getRecommendedProduct = () => {
        // Find the product with highest rank among eligible products
        const eligibleSortedProducts = sortedProducts.filter((p: any) => eligibleProductCodes.includes(p.code));

        if (eligibleSortedProducts.length > 0) {
            // Get the product with the highest rank within the primary product type
            if (primaryProduct === 'IUL') {
                const iulEligible = eligibleSortedProducts.filter((p: any) => p.productTypeCode === 'IUL');
                if (iulEligible.length > 0) return iulEligible[0].name;
            } else if (primaryProduct === 'Term') {
                const termEligible = eligibleSortedProducts.filter((p: any) => p.productTypeCode === 'TERM');
                if (termEligible.length > 0) return termEligible[0].name;
            } else if (primaryProduct === 'Whole Life') {
                const fexpEligible = eligibleSortedProducts.filter((p: any) => p.productTypeCode === 'FEXP');
                if (fexpEligible.length > 0) return fexpEligible[0].name;
            } else if (primaryProduct === 'Annuity') {
                const annuityEligible = eligibleSortedProducts.filter((p: any) => p.productTypeCode === 'ANNUITY');
                if (annuityEligible.length > 0) return annuityEligible[0].name;
            } else if (primaryProduct === 'VUL') {
                const vulEligible = eligibleSortedProducts.filter((p: any) => p.productTypeCode === 'VUL');
                if (vulEligible.length > 0) return vulEligible[0].name;
            } else if (primaryProduct === 'ADB') {
                const adbEligible = eligibleSortedProducts.filter((p: any) => p.productTypeCode === 'ADB');
                if (adbEligible.length > 0) return adbEligible[0].name;
            }

            // If no product of the primary type, return the highest ranked eligible product
            return eligibleSortedProducts[0].name;
        }

        return '';
    };

    const getPremiumString = (premium: number | undefined | null) => {
        if (premium)
            return `$${(premium * 0.8).toFixed(2).toLocaleString()} - $${(premium * 1.2).toFixed(2).toLocaleString()}`;
        return 'N/A';
    };

    const recommendedProductName = getRecommendedProduct();

    useEffect(() => {
        // Only set defaults when products are actually loaded
        if (allProducts.length === 0) {
            console.log('BestFitProduct: Waiting for products to load');
            return;
        }

        // Check if we're still loading walkthrough data
        if (parameterStore?.loadingClientData) {
            console.log('BestFitProduct: Waiting for walkthrough data to load');
            return;
        }

        // Check if user has manually overridden the selection
        const userOverridden = prequal?.userOverriddenSelection?.value;
        const savedProduct = prequal?.selectedProduct?.value;

        console.log('BestFitProduct default selection:', {
            savedProduct,
            userOverridden,
            recommendedProductName,
            currentSelectedProduct: selectedProduct,
            sortedProductsCount: sortedProducts.length,
            availableProducts: sortedProducts.slice(0, 5).map((p: any) => `${p.code} (${p.name})`),
        });

        // Get approval store for initialization
        const approval = RootStore.agentPortalStore.parameterStore?.approval;

        // Set recommendedProductCode when we have a recommendation
        if (recommendedProductName) {
            const recommendedProductData = sortedProducts.find((p: any) => p.name === recommendedProductName);
            if (recommendedProductData) {
                if (prequal?.recommendedProductCode) {
                    console.log('Setting recommendedProductCode in prequal:', recommendedProductData.code);
                    prequal.recommendedProductCode.setValue(recommendedProductData.code);
                }

                // Always update approval's recommendedProductCode to keep it in sync with prequal
                // This ensures pivot reason dropdowns update correctly when recommendations change
                if (approval && approval.recommendedProductCode) {
                    console.log('Updating recommendedProductCode in approval:', recommendedProductData.code);
                    approval.recommendedProductCode.setValue(recommendedProductData.code);
                }
            }
        }

        // Priority 1: User has manually overridden - keep their selection if it still exists
        if (userOverridden && savedProduct) {
            const savedProductExists = sortedProducts.some((p: any) => p.name === savedProduct);
            if (savedProductExists) {
                console.log('User has overridden selection, keeping saved product:', savedProduct);
                setSelectedProduct(savedProduct);
                // Set product code
                const savedProductData = sortedProducts.find((p: any) => p.name === savedProduct);
                if (savedProductData && prequal?.selectedProductCode) {
                    prequal.selectedProductCode.setValue(savedProductData.code);
                }
                return;
            } else if (recommendedProductName) {
                // If saved product was filtered out, use recommended and clear override flag
                console.log('User overridden product was filtered out, using recommended:', recommendedProductName);
                setSelectedProduct(recommendedProductName);
                if (prequal?.selectedProduct) {
                    prequal.selectedProduct.setValue(recommendedProductName);
                }
                if (prequal?.userOverriddenSelection) {
                    prequal.userOverriddenSelection.setValue(false);
                }
                const recommendedProductData = sortedProducts.find((p: any) => p.name === recommendedProductName);
                if (recommendedProductData && prequal?.selectedProductCode) {
                    prequal.selectedProductCode.setValue(recommendedProductData.code);
                }
                return;
            }
        }

        // Priority 2: For fresh forms or forms without user override - always use recommended product
        if (recommendedProductName && !userOverridden) {
            console.log('Fresh form or no user override - using recommended product:', recommendedProductName);
            setSelectedProduct(recommendedProductName);
            if (prequal?.selectedProduct) {
                prequal.selectedProduct.setValue(recommendedProductName);
            }
            const recommendedProductData = sortedProducts.find((p: any) => p.name === recommendedProductName);
            if (recommendedProductData) {
                if (prequal?.selectedProductCode) {
                    prequal.selectedProductCode.setValue(recommendedProductData.code);
                }

                // Initialize preapproval values if they're empty
                if (approval) {
                    if (approval.selectedProductCode && !approval.selectedProductCode.value) {
                        console.log('Initializing selectedProductCode in approval:', recommendedProductData.code);
                        approval.selectedProductCode.setValue(recommendedProductData.code);
                    }
                    if (approval.firstCarrierQuoted && !approval.firstCarrierQuoted.value) {
                        // Set legacy key for backward compatibility
                        const legacyKey =
                            productCodesToLegacyKeys([recommendedProductData.code])[0] || recommendedProductData.code;
                        console.log('Initializing firstCarrierQuoted in approval:', legacyKey);
                        approval.firstCarrierQuoted.setValue(legacyKey);
                    }
                    // Also set the product code field
                    if (approval.firstCarrierQuotedCode && !approval.firstCarrierQuotedCode.value) {
                        console.log('Initializing firstCarrierQuotedCode in approval:', recommendedProductData.code);
                        approval.firstCarrierQuotedCode.setValue(recommendedProductData.code);
                    }
                    if (
                        approval.agentProducts &&
                        (!approval.agentProducts.value || approval.agentProducts.value.length === 0)
                    ) {
                        // Set legacy keys for backward compatibility
                        const legacyKeys = productCodesToLegacyKeys([recommendedProductData.code]);
                        console.log('Initializing agentProducts in approval:', legacyKeys);
                        approval.agentProducts.setValue(legacyKeys);
                    }
                    // Always set the product codes field
                    if (
                        approval.agentProductCodes &&
                        (!approval.agentProductCodes.value || approval.agentProductCodes.value.length === 0)
                    ) {
                        console.log('Initializing agentProductCodes in approval:', [recommendedProductData.code]);
                        approval.agentProductCodes.setValue([recommendedProductData.code]);
                    }
                }
            }
            return;
        }

        // Priority 3: Fallback - if we have a saved product and it still exists, use it
        if (savedProduct) {
            const savedProductExists = sortedProducts.some((p: any) => p.name === savedProduct);
            if (savedProductExists) {
                console.log('Using saved product as fallback:', savedProduct);
                setSelectedProduct(savedProduct);
                const savedProductData = sortedProducts.find((p: any) => p.name === savedProduct);
                if (savedProductData && prequal?.selectedProductCode) {
                    prequal.selectedProductCode.setValue(savedProductData.code);
                }
                return;
            }
        }

        // Priority 4: Final fallback - no selection made
        console.log('No valid selection - waiting for user input');
    }, [
        recommendedProductName,
        prequal?.selectedProduct?.value,
        prequal?.userOverriddenSelection?.value,
        allProducts.length, // Add dependency on products being loaded
        parameterStore?.loadingClientData, // Add dependency on walkthrough data loading state
    ]);

    // Separate effect to load saved pivot reason values
    useEffect(() => {
        if (prequal?.pivotReason?.value && !pivotReason) {
            console.log('Loading saved pivot reason:', prequal.pivotReason.value);
            setPivotReason(prequal.pivotReason.value);
        }
        if (prequal?.pivotOtherReason?.value && !pivotOtherReason) {
            console.log('Loading saved pivot other reason:', prequal.pivotOtherReason.value);
            setPivotOtherReason(prequal.pivotOtherReason.value);
        }
    }, [prequal?.pivotReason?.value, prequal?.pivotOtherReason?.value]);

    useEffect(() => {
        if (selectedProduct && selectedProduct !== recommendedProductName) {
            setShowPivotReflexive(true);
        } else {
            setShowPivotReflexive(false);
            // Only clear pivot reasons when user actively changes selection to recommended product
            // Don't clear on initial load when there might be saved values
            if (
                selectedProduct === recommendedProductName &&
                prequal?.selectedProduct?.value === recommendedProductName
            ) {
                setPivotReason('');
                setPivotOtherReason('');
                // Also clear from parameter store
                if (prequal?.pivotReason) {
                    prequal.pivotReason.setValue('');
                }
                if (prequal?.pivotOtherReason) {
                    prequal.pivotOtherReason.setValue('');
                }
            }
        }
    }, [selectedProduct, recommendedProductName]);

    const handleProductSelect = (productName: string) => {
        setSelectedProduct(productName);

        // Save the selection immediately
        if (prequal?.selectedProduct) {
            prequal.selectedProduct.setValue(productName);
        }

        // Save the product code
        const selectedProductData = sortedProducts.find((p: any) => p.name === productName);
        if (selectedProductData) {
            if (prequal?.selectedProductCode) {
                prequal.selectedProductCode.setValue(selectedProductData.code);
            }
            // Don't update approval store here - only update when clicking "Select and Continue"
        }

        // Mark that the user has manually overridden the selection
        if (prequal?.userOverriddenSelection) {
            prequal.userOverriddenSelection.setValue(true);
        }
    };

    const handleContinue = async () => {
        setIsSaving(true);

        try {
            // First, save the product codes to prequal
            const recommendedProductData = sortedProducts.find((p: any) => p.name === recommendedProductName);
            const fallbackProductData = sortedProducts.find(
                (p: any) => categoryFullScripts.hasOwnProperty(p.productTypeCode) && p.name !== recommendedProductName,
            );
            const selectedProductData = sortedProducts.find((p: any) => p.name === selectedProduct);

            if (prequal) {
                // Save the recommended product code
                if (recommendedProductData && prequal.recommendedProductCode) {
                    prequal.recommendedProductCode.setValue(recommendedProductData.code);
                }
                // Save the fallback product code
                if (fallbackProductData && prequal.recommendedFallbackProductCode) {
                    prequal.recommendedFallbackProductCode.setValue(fallbackProductData.code);
                }
                // Save the selected product code
                if (selectedProductData && prequal.selectedProductCode) {
                    prequal.selectedProductCode.setValue(selectedProductData.code);
                }
            }

            // Save the form (all values are already in parameter store)
            await onSave(selectedProduct);

            // Determine which tab to navigate to based on selected product
            if (selectedProductData) {
                const productTypeCode = selectedProductData.productTypeCode;

                console.log('handleContinue - Selected product data:', {
                    name: selectedProductData.name,
                    code: selectedProductData.code,
                    productTypeCode: selectedProductData.productTypeCode,
                });

                // Update preapproval parameters with the selected product and recommendation data
                const approval = RootStore.agentPortalStore.parameterStore?.approval;
                console.log('Approval store exists:', !!approval);

                if (approval) {
                    // Copy over all recommendation data
                    if (recommendedProductData && approval.recommendedProductCode) {
                        console.log('Setting recommendedProductCode:', recommendedProductData.code);
                        approval.recommendedProductCode.setValue(recommendedProductData.code);
                    }
                    if (fallbackProductData && approval.recommendedFallbackProductCode) {
                        console.log('Setting fallbackProductCode:', fallbackProductData.code);
                        approval.recommendedFallbackProductCode.setValue(fallbackProductData.code);
                    }
                    if (selectedProductData && approval.selectedProductCode) {
                        console.log('Setting selectedProductCode:', selectedProductData.code);
                        approval.selectedProductCode.setValue(selectedProductData.code);
                    }

                    // Set the first carrier quoted to the selected product's legacy key
                    if (approval.firstCarrierQuoted) {
                        const legacyKey =
                            productCodesToLegacyKeys([selectedProductData.code])[0] || selectedProductData.code;
                        console.log(
                            'Setting firstCarrierQuoted:',
                            legacyKey,
                            'Current value:',
                            approval.firstCarrierQuoted.value,
                        );
                        approval.firstCarrierQuoted.setValue(legacyKey);
                        console.log('After setting firstCarrierQuoted:', approval.firstCarrierQuoted.value);
                    } else {
                        console.log('WARNING: approval.firstCarrierQuoted does not exist!');
                    }

                    // Also set the product code field
                    if (approval.firstCarrierQuotedCode) {
                        console.log('Setting firstCarrierQuotedCode:', selectedProductData.code);
                        approval.firstCarrierQuotedCode.setValue(selectedProductData.code);
                    }

                    // Set both agentProducts (legacy keys) and agentProductCodes (actual codes)
                    if (approval.agentProducts) {
                        const legacyKeys = productCodesToLegacyKeys([selectedProductData.code]);
                        console.log('Setting agentProducts (legacy) in handleContinue:', legacyKeys);
                        approval.agentProducts.setValue(legacyKeys);
                    }
                    if (approval.agentProductCodes) {
                        console.log('Setting agentProductCodes in handleContinue:', [selectedProductData.code]);
                        approval.agentProductCodes.setValue([selectedProductData.code]);
                    }

                    // If a pivot reason was selected, copy it exactly to preapproval
                    if (showPivotReflexive && pivotReason) {
                        if (approval.preapprovalPivotReason) {
                            approval.preapprovalPivotReason.setValue(pivotReason);
                        }
                        if (pivotOtherReason && approval.preapprovalPivotOtherReason) {
                            approval.preapprovalPivotOtherReason.setValue(pivotOtherReason);
                        }
                    } else {
                        // Clear pivot reasons if not needed
                        if (approval.preapprovalPivotReason) {
                            approval.preapprovalPivotReason.setValue('');
                        }
                        if (approval.preapprovalPivotOtherReason) {
                            approval.preapprovalPivotOtherReason.setValue('');
                        }
                    }
                }

                // Set the product type for quotes tab
                const quote = parameterStore.quote;

                // Check if this product has a quote mapping
                const carrierProductMapping = getCarrierProductFromCode(selectedProductData.code);

                let quoteProductType = '';
                let targetTab = '2'; // Default to Pre-Approvals tab

                // If there's a mapping and it's not explicitly PreApproval, go to quotes
                if (carrierProductMapping && carrierProductMapping.productType !== 'PreApproval') {
                    quoteProductType = carrierProductMapping.productType;
                    targetTab = '1'; // Quotes tab
                } else if (productTypeCode === 'ANNUITY') {
                    // Annuities always go to pre-approvals
                    targetTab = '2';
                } else {
                    // No mapping or explicitly PreApproval - go to pre-approvals
                    targetTab = '2';
                }

                // Set the product type in quotes if navigating to quotes tab
                if (targetTab === '1' && quote?.productType) {
                    console.log('Setting quote productType:', quoteProductType);
                    quote.productType.setValue(quoteProductType);
                }

                // Navigate to the appropriate tab with query parameter for product type
                const newPath = generatePath('/agent/form/:email/:formId/:tabIndex', {
                    email: params.email || parameterStore?.contact,
                    formId: params.formId || parameterStore?.formid,
                    tabIndex: targetTab,
                });

                // Add query parameter for the selected product type if going to quotes
                const finalPath =
                    targetTab === '1' && quoteProductType
                        ? `${newPath}?productType=${encodeURIComponent(quoteProductType)}`
                        : newPath;

                history.push(finalPath);
            }
        } catch (error) {
            console.error('Error saving form:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const isProductEligible = (product: any) => {
        // All products shown are already filtered for eligibility
        return true;
    };

    const renderProductGroup = (products: any[] | undefined, title: string) => {
        if (!products || products.length === 0) return null;

        return (
            <div className={classes.productCategory}>
                <Typography className={classes.productCategoryTitle}>{title}</Typography>
                {products.map((product) => {
                    const isRecommended = product.name === recommendedProductName;
                    const isEligible = isProductEligible(product);
                    const isSelected = selectedProduct === product.name;

                    return (
                        <div
                            key={product.code || product.name}
                            className={`${classes.productOption} ${isSelected ? classes.selectedProduct : ''}`}
                            onClick={() => handleProductSelect(product.name)}
                        >
                            {isRecommended && <StarIcon className={classes.goldStar} />}
                            <Radio
                                checked={isSelected}
                                color="primary"
                                size="small"
                                className={isSelected ? classes.radioButtonSelected : classes.radioButton}
                            />
                            <Typography style={{flex: 1}}>{product.name}</Typography>
                        </div>
                    );
                })}
            </div>
        );
    };

    const getSelectedProductOneLiner = () => {
        // Find the selected product's category
        const selectedProductData = sortedProducts.find((p: any) => p.name === selectedProduct);
        if (selectedProductData) {
            const productTypeCode = selectedProductData.productTypeCode;
            return categoryOneLiners[productTypeCode] || '';
        }
        return '';
    };

    const getSelectedProductFullScript = () => {
        // Find the selected product's category
        const selectedProductData = sortedProducts.find((p: any) => p.name === selectedProduct);
        if (selectedProductData) {
            const productTypeCode = selectedProductData.productTypeCode;
            return categoryFullScripts[productTypeCode] || '';
        }
        return prequal?.recommendedProductPrimaryScript?.value || '';
    };

    const getPivotReasonOptions = (): PivotReasonOption[] => {
        // Find the recommended product's code
        const recommendedProductData = sortedProducts.find((p: any) => p.name === recommendedProductName);
        if (recommendedProductData && PIVOT_REASONS[recommendedProductData.code]) {
            return PIVOT_REASONS[recommendedProductData.code];
        }

        // Use default options if no specific options found
        return PIVOT_REASONS['default'];
    };

    const needsOtherReasonInput = () => {
        if (!pivotReason) return false;

        const options = getPivotReasonOptions();
        const selectedOption = options.find((opt) => opt.text === pivotReason);
        return selectedOption?.requiresExplanation || false;
    };

    const isFormValid = () => {
        // Must have a selected product
        if (!selectedProduct) return false;

        // If showing pivot section
        if (showPivotReflexive) {
            // Must have selected a reason
            if (!pivotReason) return false;

            // If the reason requires explanation, must have explanation text
            if (needsOtherReasonInput() && !pivotOtherReason?.trim()) {
                return false;
            }
        }

        return true;
    };

    /**
     * For displaying data from walkthrough
     */
    const [customerSelectedQuote, setCustomerSelected] = useState({
        policyType: '',
        cashValue: '',
        coverageAmount: '',
        termCoverageAmount: '',
        permCoverageAmount: '',
        termPremium: '',
        permPremium: '',
        monthlyPayment: '',
        yearCoverage: '',
        permCarrier: '',
        termCarrier: '',
    });

    // Determine if customer selected a quote in walkthrough
    const [customerPortalData, setCustomerPortalData] = useState<Customer | null>(null);
    useEffect(() => {
        const fetchCustomerPortalData = async () => {
            if (!parameterStore?.approval?.emailAddress?.value) {
                return;
            }
            const data = await CustomerPortalAPI.GetCustomerPortalData(parameterStore?.approval?.emailAddress?.value);
            setCustomerPortalData(data?.customerByEmail);
        };
        fetchCustomerPortalData();
    }, [parameterStore?.approval?.emailAddress?.value]);

    useEffect(() => {
        if (customerQuoteData && customerQuoteData.length) {
            const quoteMap = new Map();
            customerQuoteData[0].v.properties.forEach((prop) => {
                quoteMap.set(prop.property, prop.value);
            });
            if (quoteMap.get('user_selected_category') === 'VUL') {
                setCustomerSelected({
                    ...customerSelectedQuote,
                    policyType: quoteMap.get('user_selected_category'),
                    cashValue: (quoteMap.get('vul') || 0).toFixed(2),
                    coverageAmount: '100000',
                    monthlyPayment: quoteMap.get('contribution'),
                    permCarrier: quoteMap.get('user_selected_vul_carrier'),
                });
            } else {
                setCustomerSelected({
                    ...customerSelectedQuote,
                    policyType: quoteMap.get('selected_product_type'),
                    cashValue: quoteMap.get('user_selected_cv_amt'),
                    termCoverageAmount: quoteMap.get('user_selected_term_coverage'),
                    permCoverageAmount: quoteMap.get('user_selected_perm_coverage'),
                    termPremium: quoteMap.get('user_selected_term_premium'),
                    permPremium: quoteMap.get('user_selected_perm_premium'),
                    yearCoverage: quoteMap.get('term_period'),
                    permCarrier: quoteMap.get('user_selected_perm_carrier'),
                    termCarrier: quoteMap.get('user_selected_term_carrier'),
                });
            }
        }
    }, [customerQuoteData]);

    // Check if we're in Prosper Flow based on the walkthrough data
    const walkthroughRawData = (parameterStore as any)._walkthroughRawData;
    const useProsperFlow = walkthroughRawData?.userData?.useProsperFlow === true;

    // Get the recommendation type from walkthrough data - if Prosper Flow, show IUL instead of VUL
    const actualWalkthroughRecommendation = walkthroughRawData?.quoteData?.productTypeRecommendation;
    let walkthroughRecommendedProductType =
        useProsperFlow && actualWalkthroughRecommendation === 'VUL' ? 'IUL' : actualWalkthroughRecommendation;

    let walkthroughRecommendedQuote:
        | {faceAmount: number; modalPremium: number; surrenderValue?: number; termPeriod?: number; carrier?: string}
        | undefined;

    if (useProsperFlow && walkthroughRawData?.quoteData?.prosper?.quote) {
        // Use Prosper IUL quote when in Prosper Flow
        const {quote: prosperQuote, product: prosperProduct} = walkthroughRawData.quoteData.prosper;
        walkthroughRecommendedQuote = {
            faceAmount: prosperQuote.faceAmount,
            modalPremium: prosperQuote.modalPremium,
            surrenderValue: prosperQuote.surrenderValue || prosperQuote.cashValueAt65,
            carrier: prosperProduct.carrierName,
        };
        // Ensure we show IUL as the recommended product type
        walkthroughRecommendedProductType = 'IUL';
    } else if (walkthroughRecommendedProductType === 'IUL') {
        walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.iul?.quote;
        if (walkthroughRecommendedQuote) {
            walkthroughRecommendedQuote.carrier = walkthroughRawData?.quoteData?.iul?.product?.carrierName;
        }
    } else if (walkthroughRecommendedProductType === 'TERM') {
        walkthroughRecommendedQuote = walkthroughRawData?.quoteData?.term?.quote;
        if (walkthroughRecommendedQuote) {
            walkthroughRecommendedQuote.carrier = walkthroughRawData?.quoteData?.term?.product?.carrierName;
        }
    } else if (walkthroughRecommendedProductType === 'VUL') {
        // VUL doesn't have quote in the response structure
        walkthroughRecommendedQuote = undefined;
    }

    // Show loading state if no products available
    if (allProducts.length === 0) {
        return (
            <div className={classes.container}>
                <Grid container className={classes.header}>
                    <Grid item>
                        <div className={classes.sectionTitle}>
                            <span className={classes.sectionNumber}>5</span>
                            <Typography className={classes.sectionTitle} variant="h5">
                                Best Fit Product
                            </Typography>
                        </div>
                    </Grid>
                </Grid>
                <div style={{padding: '40px', textAlign: 'center'}}>
                    <Typography variant="body1" style={{color: '#666'}}>
                        Loading products...
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Grid container className={classes.header}>
                <Grid item>
                    <div className={classes.sectionTitleBox}>
                        <span className={classes.sectionNumber}>5</span>
                        <Typography className={classes.sectionTitle} variant="h5">
                            Best Fit Product
                        </Typography>
                    </div>
                </Grid>
                <Grid item>
                    <Button
                        disabled={loading || isSaving}
                        variant="default"
                        color="primary"
                        size="lg"
                        className={classes.saveAndUpdateButton}
                        onClick={async () => {
                            setIsSaving(true);
                            try {
                                await onSave(selectedProduct);
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                    >
                        {isSaving ? 'SAVING...' : 'SAVE & UPDATE RECOMMENDATIONS'}
                    </Button>
                </Grid>
            </Grid>

            {(customerSelectedQuote.policyType || walkthroughRecommendedProductType) && (
                <div style={{paddingBottom: '24px'}}>
                    <Grid container spacing={4}>
                        {walkthroughRecommendedProductType && (
                            <Grid item xs={6}>
                                <CardContent className={classes.recommendationItem}>
                                    <div>
                                        <strong className={classes.recommendationItemTitle}>
                                            Walkthrough Suggested{' '}
                                        </strong>
                                    </div>
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Product Type: </strong>
                                        {walkthroughRecommendedProductType}
                                    </div>
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Carrier: </strong>
                                        {walkthroughRecommendedQuote?.carrier}
                                    </div>
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Coverage Amount:</strong> $
                                        {walkthroughRecommendedQuote?.faceAmount?.toLocaleString() || 'N/A'}
                                    </div>
                                    {walkthroughRecommendedQuote?.surrenderValue && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Cash Value:</strong> $
                                            {walkthroughRecommendedQuote?.surrenderValue?.toLocaleString()}
                                        </div>
                                    )}
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Monthly Payment: </strong>
                                        {getPremiumString(walkthroughRecommendedQuote?.modalPremium)}
                                    </div>
                                    {walkthroughRecommendedQuote?.termPeriod && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Term Period:</strong> {walkthroughRecommendedQuote.termPeriod} yrs
                                        </div>
                                    )}
                                </CardContent>
                            </Grid>
                        )}
                        {customerPortalData && customerSelectedQuote.policyType && (
                            <Grid item xs={6}>
                                <CardContent className={classes.recommendationItem}>
                                    <div>
                                        <strong className={classes.recommendationItemTitle}>Client Selected </strong>
                                    </div>
                                    <div style={{margin: 2, marginTop: 8}}>
                                        <strong>Product Type: </strong>
                                        {useProsperFlow ? 'IUL' : customerSelectedQuote.policyType}
                                    </div>
                                    {customerSelectedQuote.termCarrier && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Carrier: </strong>
                                            {customerSelectedQuote.termCarrier}
                                        </div>
                                    )}
                                    {customerSelectedQuote.permCarrier && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Carrier: </strong>
                                            {customerSelectedQuote.permCarrier}
                                        </div>
                                    )}
                                    {customerSelectedQuote.coverageAmount && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Coverage Amount:</strong> $
                                            {Number(customerSelectedQuote.coverageAmount)?.toLocaleString()}
                                        </div>
                                    )}
                                    {customerSelectedQuote.termCoverageAmount && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Term Coverage:</strong> $
                                            {Number(customerSelectedQuote.termCoverageAmount)?.toLocaleString()}
                                        </div>
                                    )}
                                    {customerSelectedQuote.permCoverageAmount && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Perm Coverage:</strong> $
                                            {Number(customerSelectedQuote.permCoverageAmount)?.toLocaleString()}
                                        </div>
                                    )}
                                    {customerSelectedQuote.cashValue && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Cash Value:</strong> $
                                            {Number(customerSelectedQuote.cashValue)?.toLocaleString()}
                                        </div>
                                    )}
                                    {customerSelectedQuote.monthlyPayment && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Monthly Payment: </strong>
                                            {getPremiumString(Number(customerSelectedQuote.monthlyPayment))}
                                        </div>
                                    )}
                                    {customerSelectedQuote.termPremium && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Term Premium: </strong>
                                            {getPremiumString(Number(customerSelectedQuote.termPremium))}
                                        </div>
                                    )}
                                    {customerSelectedQuote.permPremium && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Perm Premium: </strong>
                                            {getPremiumString(Number(customerSelectedQuote.permPremium))}
                                        </div>
                                    )}
                                    {customerSelectedQuote.yearCoverage && (
                                        <div style={{margin: 2, marginTop: 8}}>
                                            <strong>Term Period:</strong> {customerSelectedQuote.yearCoverage} yrs
                                        </div>
                                    )}
                                </CardContent>
                            </Grid>
                        )}
                    </Grid>
                </div>
            )}

            {selectedProduct && (
                <div className={classes.blueInfoBox}>
                    <div className={classes.productNameWithInfo}>
                        <Tooltip
                            title={getSelectedProductFullScript() || 'No script available'}
                            placement="bottom-start"
                            arrow
                            classes={{tooltip: classes.tooltip}}
                        >
                            <InfoOutlined className={classes.infoIcon} />
                        </Tooltip>
                        <Typography className={classes.selectedProductTitle}>{selectedProduct}</Typography>
                    </div>
                    <Typography className={classes.scriptText}>{getSelectedProductOneLiner()}</Typography>
                </div>
            )}

            {fallbackProduct && (
                <div className={classes.fallbackSection}>
                    <InfoOutlined className={classes.infoIcon} />
                    <Typography className={classes.fallbackText}>
                        <span>Fallback: {fallbackDisplayNames[fallbackProduct] || fallbackProduct}</span>
                        <span style={{margin: '0 4px'}}>•</span>
                        <span>
                            {productFallbackTriggers[fallbackProduct] ||
                                fallbackTriggers ||
                                '[Fallback triggers placeholder]'}
                        </span>
                    </Typography>
                </div>
            )}

            <div className={classes.productListSection}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        {renderProductGroup(iulProducts, 'IUL')}
                        {renderProductGroup(vulProducts, 'VUL')}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {renderProductGroup(termProducts, 'Term')}
                        {renderProductGroup(annuityProducts, 'Annuity')}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {renderProductGroup(finalExpenseProducts, 'Whole Life')}
                        {renderProductGroup(adbProducts, 'ADB')}
                    </Grid>
                </Grid>
            </div>

            {showPivotReflexive && (
                <Grid container justify="space-between" alignItems="flex-end" className={classes.pivotReflexive}>
                    <Grid item xs={12} md={5} lg={3}>
                        <InputLabel className={classes.pivotLabel}>
                            <span style={{color: 'red'}}>* </span> Reason For Not Choosing The Recommended Product
                        </InputLabel>
                        <Select
                            fullWidth
                            variant="outlined"
                            value={pivotReason}
                            onChange={(e) => {
                                const newReason = e.target.value as string;
                                setPivotReason(newReason);
                                // Save to parameter store immediately
                                if (prequal?.pivotReason) {
                                    prequal.pivotReason.setValue(newReason);
                                }
                                // Clear the explanation if switching to a reason that doesn't need it
                                const options = getPivotReasonOptions();
                                const selectedOption = options.find((opt) => opt.text === newReason);
                                if (!selectedOption?.requiresExplanation && prequal?.pivotOtherReason) {
                                    setPivotOtherReason('');
                                    prequal.pivotOtherReason.setValue('');
                                }
                            }}
                            displayEmpty
                            style={{height: 48}}
                        >
                            <MenuItem value="">
                                <em>Select a reason...</em>
                            </MenuItem>
                            {getPivotReasonOptions().map((option) => (
                                <MenuItem key={option.text} value={option.text}>
                                    {option.text}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    {needsOtherReasonInput() && (
                        <Grid item xs={12} md={6} lg={8}>
                            <InputLabel className={classes.pivotLabel} style={{marginTop: 10}}>
                                <span style={{color: 'red'}}>* </span>Additional Details for not choosing the
                                recommended product
                            </InputLabel>
                            <TextField
                                fullWidth
                                multiline
                                variant="outlined"
                                placeholder="Please explain..."
                                value={pivotOtherReason}
                                onChange={(e) => {
                                    const newExplanation = e.target.value;
                                    setPivotOtherReason(newExplanation);
                                    // Save to parameter store immediately
                                    if (prequal?.pivotOtherReason) {
                                        prequal.pivotOtherReason.setValue(newExplanation);
                                    }
                                }}
                                InputProps={{
                                    style: {height: 48},
                                }}
                                required
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            <Grid container justify="center" alignItems="center">
                <Grid item xs={10} sm={6} md={4}>
                    <MUIButton
                        variant="contained"
                        color="primary"
                        size="large"
                        className={classes.continueButton}
                        onClick={handleContinue}
                        disabled={!isFormValid() || isSaving || loading}
                    >
                        {isSaving ? 'SAVING...' : 'SELECT & CONTINUE'}
                    </MUIButton>
                </Grid>
            </Grid>
        </div>
    );
};
